import Konva from "konva";
import { ContentBounds, DisplaySize, EditorObject } from "../types/editorTypes";

interface GetContentBoundsParams {
  displaySize: DisplaySize;
  bandImg: HTMLImageElement | null;
  bandScale: number;
  bandScaleY: number;
  getBandX: () => number;
  getBandY: () => number;
}

/**
 * コンテンツの実際の範囲を計算（画像 + 帯の範囲）
 */
export const getContentBounds = ({
  displaySize,
  bandImg,
  bandScale,
  bandScaleY,
  getBandX,
  getBandY,
}: GetContentBoundsParams): ContentBounds => {
  // 基本は画像の範囲
  let minX = displaySize.offsetX;
  let minY = displaySize.offsetY;
  let maxX = displaySize.offsetX + displaySize.width;
  let maxY = displaySize.offsetY + displaySize.height;

  // 帯がある場合、その範囲も考慮
  if (bandImg && displaySize.width > 0) {
    const bandY = getBandY();
    const scaledBandWidth = displaySize.width * bandScale;
    const scaledBandHeight = (bandImg.height / bandImg.width) * displaySize.width * bandScale * bandScaleY;
    const bandX = getBandX();

    minX = Math.min(minX, bandX);
    minY = Math.min(minY, bandY);
    maxX = Math.max(maxX, bandX + scaledBandWidth);
    maxY = Math.max(maxY, bandY + scaledBandHeight);
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

interface DownloadImageParams {
  stageRef: React.RefObject<Konva.Stage>;
  bounds: ContentBounds;
  filename?: string;
}

/**
 * キャンバスを画像としてダウンロード
 */
export const downloadCanvasAsImage = ({
  stageRef,
  bounds,
  filename = 'maisoku_edited.jpg',
}: DownloadImageParams): void => {
  if (!stageRef.current || bounds.width <= 0 || bounds.height <= 0) return;

  const uri = stageRef.current.toDataURL({
    pixelRatio: 2,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  });

  const link = document.createElement('a');
  link.download = filename;
  link.href = uri;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface DownloadPDFParams {
  stageRef: React.RefObject<Konva.Stage>;
  bounds: ContentBounds;
  filename?: string;
  margin?: number;
}

/**
 * キャンバスをPDFとしてダウンロード
 */
export const downloadCanvasAsPDF = async ({
  stageRef,
  bounds,
  filename = 'maisoku_edited.pdf',
  margin = 10,
}: DownloadPDFParams): Promise<void> => {
  if (!stageRef.current || bounds.width <= 0 || bounds.height <= 0) return;

  // 動的にjsPDFをインポート
  const { jsPDF } = await import('jspdf');

  const uri = stageRef.current.toDataURL({
    pixelRatio: 2,
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  });

  // 画像のアスペクト比を計算
  const imgWidth = bounds.width;
  const imgHeight = bounds.height;
  const aspectRatio = imgWidth / imgHeight;

  // A4サイズ（mm）
  const a4Width = 210;
  const a4Height = 297;

  // 画像がA4に収まるようにサイズを計算
  let pdfWidth: number;
  let pdfHeight: number;
  let orientation: 'portrait' | 'landscape';

  if (aspectRatio > 1) {
    // 横長の場合は横向き
    orientation = 'landscape';
    pdfWidth = a4Height; // 297mm
    pdfHeight = a4Width; // 210mm
  } else {
    // 縦長の場合は縦向き
    orientation = 'portrait';
    pdfWidth = a4Width; // 210mm
    pdfHeight = a4Height; // 297mm
  }

  // 余白を設定
  const contentWidth = pdfWidth - (margin * 2);
  const contentHeight = pdfHeight - (margin * 2);

  // 画像のフィットサイズを計算
  let fitWidth: number;
  let fitHeight: number;
  if (contentWidth / contentHeight > aspectRatio) {
    // 高さに合わせる
    fitHeight = contentHeight;
    fitWidth = fitHeight * aspectRatio;
  } else {
    // 幅に合わせる
    fitWidth = contentWidth;
    fitHeight = fitWidth / aspectRatio;
  }

  // 中央に配置
  const x = margin + (contentWidth - fitWidth) / 2;
  const y = margin + (contentHeight - fitHeight) / 2;

  const pdf = new jsPDF({
    orientation,
    unit: 'mm',
    format: 'a4',
  });

  pdf.addImage(uri, 'JPEG', x, y, fitWidth, fitHeight);
  pdf.save(filename);
};

interface MultiPagePDFParams {
  imageDataUrls: string[];
  filename?: string;
  margin?: number;
}

/**
 * 複数ページのPDFをダウンロード
 */
export const downloadMultiPagePDF = async ({
  imageDataUrls,
  filename = 'maisoku_edited.pdf',
  margin = 10,
}: MultiPagePDFParams): Promise<void> => {
  if (imageDataUrls.length === 0) return;

  // 動的にjsPDFをインポート
  const { jsPDF } = await import('jspdf');

  let pdf: InstanceType<typeof jsPDF> | null = null;

  for (let i = 0; i < imageDataUrls.length; i++) {
    const uri = imageDataUrls[i];

    // 画像を読み込んでサイズを取得
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = uri;
    });

    const imgWidth = img.width;
    const imgHeight = img.height;
    const aspectRatio = imgWidth / imgHeight;

    // A4サイズ（mm）
    const a4Width = 210;
    const a4Height = 297;

    // 画像がA4に収まるようにサイズを計算
    let pdfWidth: number;
    let pdfHeight: number;
    let orientation: 'portrait' | 'landscape';

    if (aspectRatio > 1) {
      orientation = 'landscape';
      pdfWidth = a4Height;
      pdfHeight = a4Width;
    } else {
      orientation = 'portrait';
      pdfWidth = a4Width;
      pdfHeight = a4Height;
    }

    // 余白を設定
    const contentWidth = pdfWidth - (margin * 2);
    const contentHeight = pdfHeight - (margin * 2);

    // 画像のフィットサイズを計算
    let fitWidth: number;
    let fitHeight: number;
    if (contentWidth / contentHeight > aspectRatio) {
      fitHeight = contentHeight;
      fitWidth = fitHeight * aspectRatio;
    } else {
      fitWidth = contentWidth;
      fitHeight = fitWidth / aspectRatio;
    }

    // 中央に配置
    const x = margin + (contentWidth - fitWidth) / 2;
    const y = margin + (contentHeight - fitHeight) / 2;

    if (i === 0) {
      // 最初のページでPDFを作成
      pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: 'a4',
      });
    } else if (pdf) {
      // 2ページ目以降は新しいページを追加
      pdf.addPage('a4', orientation);
    }

    if (pdf) {
      pdf.addImage(uri, 'JPEG', x, y, fitWidth, fitHeight);
    }
  }

  if (pdf) {
    pdf.save(filename);
  }
};

interface OffScreenRenderParams {
  sourceImageFile: File;
  displaySize: DisplaySize;
  objects: EditorObject[];
  bandImg?: HTMLImageElement | null;
  bandImageUrl?: string; // 帯画像のURL（bandImgが指定されていない場合に使用）
  bandScale?: number;
  bandScaleY?: number;
  bandY?: number;
  bandPosition?: 'top' | 'bottom';
  bandXOffset?: number;
  bandYOffset?: number;
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * 画像ファイルを読み込んでHTMLImageElementを返す
 */
export const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * URLから画像を読み込んでHTMLImageElementを返す
 */
export const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * オフスクリーンでKonva Stageを作成し、オブジェクトを描画してデータURLを返す
 */
export const renderPageOffScreen = async ({
  sourceImageFile,
  displaySize,
  objects,
  bandImg,
  bandImageUrl,
  bandScale = 1,
  bandScaleY = 1,
  bandY,
  bandPosition = 'top',
  bandXOffset = 0,
  bandYOffset = 0,
  canvasWidth,
  canvasHeight,
}: OffScreenRenderParams): Promise<string> => {
  // ソース画像を読み込み
  const sourceImg = await loadImageFromFile(sourceImageFile);

  // 帯画像を取得（bandImgが指定されていない場合はURLから読み込み）
  let actualBandImg: HTMLImageElement | null = bandImg || null;
  if (!actualBandImg && bandImageUrl) {
    try {
      actualBandImg = await loadImageFromUrl(bandImageUrl);
    } catch (error) {
      console.error('Failed to load band image from URL:', error);
    }
  }

  // 帯のY位置を計算（bandYが指定されていない場合は計算）
  let actualBandY = bandY;
  if (actualBandY === undefined && actualBandImg && displaySize.height > 0) {
    const scaledBandHeight = (actualBandImg.height / actualBandImg.width) * displaySize.width * bandScale * bandScaleY;
    if (bandPosition === 'top') {
      actualBandY = displaySize.offsetY + bandYOffset;
    } else {
      actualBandY = displaySize.offsetY + displaySize.height - scaledBandHeight + bandYOffset;
    }
  }

  // オフスクリーン用のコンテナを作成
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    // Konva Stageを作成
    const stage = new Konva.Stage({
      container,
      width: canvasWidth,
      height: canvasHeight,
    });

    const layer = new Konva.Layer();
    stage.add(layer);

    // 背景画像を描画
    const bgImage = new Konva.Image({
      image: sourceImg,
      x: displaySize.offsetX,
      y: displaySize.offsetY,
      width: displaySize.width,
      height: displaySize.height,
    });
    layer.add(bgImage);

    // 帯画像を描画（存在する場合）
    if (actualBandImg && actualBandY !== undefined) {
      const bandKonvaImg = new Konva.Image({
        image: actualBandImg,
        x: displaySize.offsetX + (displaySize.width - displaySize.width * bandScale) / 2 + bandXOffset,
        y: actualBandY,
        width: displaySize.width * bandScale,
        height: (actualBandImg.height / actualBandImg.width) * displaySize.width * bandScale * bandScaleY,
      });
      layer.add(bandKonvaImg);
    }

    // エディタオブジェクトを描画
    for (const obj of objects) {
      await renderObjectToLayer(layer, obj);
    }

    // 注: ダウンロード時はウォーターマークを含めない（プレビュー専用）

    layer.draw();

    // コンテンツ境界を計算
    const bounds = getContentBounds({
      displaySize,
      bandImg: actualBandImg,
      bandScale,
      bandScaleY,
      getBandX: () => displaySize.offsetX + (displaySize.width - displaySize.width * bandScale) / 2 + bandXOffset,
      getBandY: () => actualBandY ?? displaySize.offsetY,
    });

    // データURLを取得
    const dataUrl = stage.toDataURL({
      pixelRatio: 2,
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
    });

    // クリーンアップ
    stage.destroy();
    return dataUrl;
  } finally {
    document.body.removeChild(container);
  }
};

/**
 * EditorObjectをKonva Layerに描画
 */
const renderObjectToLayer = async (layer: Konva.Layer, obj: EditorObject): Promise<void> => {
  switch (obj.type) {
    case 'text': {
      // 背景または枠線がある場合
      if ((obj.backgroundColor && obj.backgroundColor !== 'transparent') || obj.stroke) {
        const bgRect = new Konva.Rect({
          x: obj.x,
          y: obj.y,
          width: obj.width || 150,
          height: obj.height || 24,
          fill: obj.backgroundColor && obj.backgroundColor !== 'transparent' ? obj.backgroundColor : undefined,
          stroke: obj.stroke,
          strokeWidth: obj.strokeWidth,
          rotation: obj.rotation || 0,
        });
        layer.add(bgRect);
      }
      // テキスト
      const text = new Konva.Text({
        x: obj.x,
        y: obj.y,
        text: obj.text || '',
        fontSize: obj.fontSize,
        fontFamily: obj.fontFamily || 'sans-serif',
        fontStyle: obj.fontStyle || 'normal',
        align: obj.textAlign || 'left',
        verticalAlign: obj.verticalAlign || 'top',
        fill: obj.textColor,
        width: obj.width || 150,
        height: obj.height,
        wrap: 'char',
        rotation: obj.rotation || 0,
      });
      layer.add(text);
      break;
    }

    case 'rect': {
      // 四角形
      const group = new Konva.Group({
        x: obj.x,
        y: obj.y,
        rotation: obj.rotation || 0,
      });

      const rect = new Konva.Rect({
        x: 0,
        y: 0,
        width: obj.width,
        height: obj.height,
        fill: obj.backgroundColor,
        stroke: obj.stroke,
        strokeWidth: obj.strokeWidth,
      });
      group.add(rect);

      // 四角形内テキスト
      if (obj.text) {
        const text = new Konva.Text({
          x: 4,
          y: 4,
          text: obj.text,
          fontSize: obj.fontSize || 14,
          fontFamily: obj.fontFamily || 'sans-serif',
          fontStyle: obj.fontStyle || 'normal',
          align: obj.textAlign || 'left',
          verticalAlign: obj.verticalAlign || 'top',
          fill: obj.textColor || '#000000',
          width: (obj.width || 100) - 8,
          height: (obj.height || 50) - 8,
          wrap: 'char',
        });
        group.add(text);
      }

      layer.add(group);
      break;
    }

    case 'line':
    case 'marker': {
      const line = new Konva.Line({
        points: obj.points,
        stroke: obj.stroke,
        strokeWidth: obj.strokeWidth,
        lineCap: obj.type === 'marker' ? 'round' : 'butt',
        lineJoin: obj.type === 'marker' ? 'round' : 'miter',
        opacity: obj.type === 'marker' ? 0.5 : 1,
        rotation: obj.rotation || 0,
      });
      layer.add(line);
      break;
    }

    case 'image': {
      if (obj.imageUrl) {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.crossOrigin = 'anonymous';
          image.src = obj.imageUrl!;
        });

        const konvaImg = new Konva.Image({
          image: img,
          x: obj.x,
          y: obj.y,
          width: obj.width,
          height: obj.height,
          rotation: obj.rotation || 0,
        });
        layer.add(konvaImg);
      }
      break;
    }
  }
};
